import { FilesViewer } from 'components/FileViewer';
import { HtmlIframeViewer } from 'components/HtmlIframeViewer';
import {
  getFileTypeFromContentType,
  getFileTypeFromFileName
} from 'core/services/storage/utils';
import { S3Bucket } from 'core/services/amplify/config/constants';
import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Typography,
  SxProps
} from '@mui/material';
import { Button } from 'system/atoms';
import { useAmplifyFileURL } from 'core/services/amplify/storage/useAmplifyFileURL';
import { useAmplifyFileContentType } from 'core/services/amplify/storage/useAmplifyFileContentType';
import { AlertCard } from 'common/ui/AlertCard';
import { getCvFileName } from './getCvFileName';

const cvDownloadButtonSx: SxProps = {
  fontWeight: '500'
};

const cvContainerSx: SxProps = {
  border: '1px solid',
  borderColor: 'customGrey.medium'
};

const REDACTED_VALUE = 'REDACTED';

type Props = {
  cvS3Key: string;
  cvParsedText: string | null | undefined;
};

export const CvViewer = ({ cvS3Key, cvParsedText }: Props) => {
  const isRedacted = cvS3Key === REDACTED_VALUE;

  const { url } = useAmplifyFileURL({
    path: isRedacted ? null : cvS3Key,
    bucket: S3Bucket.CANDIDATE_BATCH
  });

  // A key without an extension is typed by the stored object's Content-Type.
  const fileTypeFromKey = getFileTypeFromFileName(cvS3Key || '');
  const { contentType } = useAmplifyFileContentType({
    path: isRedacted || fileTypeFromKey ? null : cvS3Key,
    bucket: S3Bucket.CANDIDATE_BATCH
  });

  const isTypeResolved = Boolean(fileTypeFromKey) || contentType !== undefined;
  const fileType = fileTypeFromKey ?? getFileTypeFromContentType(contentType);
  const fileName = getCvFileName(cvS3Key || '', fileType);

  // Browsers cannot display Word files, so their link always downloads.
  const isWord = fileType === 'docx' || fileType === 'doc';
  const { url: downloadUrl } = useAmplifyFileURL({
    path: isRedacted || !isWord ? null : cvS3Key,
    bucket: S3Bucket.CANDIDATE_BATCH,
    downloadAs: fileName
  });
  const linkUrl = isWord ? downloadUrl : url;

  if (isRedacted) {
    return (
      <AlertCard type="info" title="CV has been removed">
        This candidate's CV has been redacted for data protection purposes.
      </AlertCard>
    );
  }

  if (!url || !linkUrl || !isTypeResolved) {
    return <Skeleton variant="rectangular" height="100%" width="100%" />;
  }

  return (
    <>
      <Stack direction="row">
        <Button variant="link" sx={cvDownloadButtonSx} href={linkUrl.href}>
          {fileName}
        </Button>
      </Stack>
      {isWord && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography whiteSpace="pre-line" sx={{ ...cvContainerSx, p: 2 }}>
            {cvParsedText}
          </Typography>
        </>
      )}
      {fileType === 'html' && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={cvContainerSx}>
            <HtmlIframeViewer
              url={url.href}
              title={fileName}
              style={{ height: '600px' }}
            />
          </Box>
        </>
      )}
      {!isWord && fileType !== 'html' && (
        <Box sx={cvContainerSx}>
          <FilesViewer
            docs={[
              {
                uri: url.href,
                fileName: cvS3Key,
                fileType
              }
            ]}
            style={{
              width: '100%'
            }}
          />
        </Box>
      )}
    </>
  );
};
